<script lang="ts" setup>
import { ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useHistoryPage } from '../composables/useHistoryPage'
import type { HistoryCommonConfig, HistoryDeleteModalTheme } from '../configs/types'
import HistoryEditOverlay from './HistoryEditOverlay.vue'
import HistoryTabPanel from './HistoryTabPanel.vue'
import HistoryTopBar from './HistoryTopBar.vue'
import { resources } from '@/config/brands'

const props = defineProps<{
  config: HistoryCommonConfig
  deleteModalTheme: HistoryDeleteModalTheme
}>()

const coverPlaceholder = resources.bookCoverPlaceholder
const infiniteRef = ref<HTMLElement>()

const {
  from,
  activeTab,
  editMode,
  selectedIds,
  deleteConfirmVisible,
  library,
  readHistory,
  isAllSelected,
  loadCurrent,
  switchTab,
  enterEdit,
  exitEdit,
  toggleSelectAll,
  openDeleteConfirm,
  closeDeleteConfirm,
  confirmDelete,
  onItemClick,
  onCoverError,
  toBookRowView,
} = useHistoryPage(props.config, coverPlaceholder)

useInfiniteScroll(infiniteRef, async () => {
  await loadCurrent()
}, { distance: 50 })
</script>

<template>
  <div
    class="h-100dvh flex flex-col overflow-hidden bg-n-bg-global pb-24"
  >
    <div v-if="from" class="flex-shrink-0">
      <BasicNavbar />
      <div class="h-5" />
    </div>

    <HistoryTopBar
      :active-tab="activeTab"
      :edit-mode="editMode"
      @switch-tab="switchTab"
      @enter-edit="enterEdit"
      @exit-edit="exitEdit"
    />

    <div
      ref="infiniteRef"
      class="flex-1 overflow-auto mx-10px mb-10px rounded-8px rr-card px-10px pt-16px pb-16px"
    >
      <HistoryTabPanel
        v-if="activeTab === 'library'"
        tab="library"
        :items="library.items"
        :fetching="library.fetching"
        :ending="library.ending"
        :page="library.page"
        :edit-mode="editMode"
        :selected-ids="selectedIds"
        :config="config"
        :cover-placeholder="coverPlaceholder"
        :to-book-row-view="toBookRowView"
        @item-click="onItemClick"
        @cover-error="onCoverError"
      />

      <HistoryTabPanel
        v-else
        tab="history"
        :items="readHistory.items"
        :fetching="readHistory.fetching"
        :ending="readHistory.ending"
        :page="readHistory.page"
        :edit-mode="editMode"
        :selected-ids="selectedIds"
        :config="config"
        :cover-placeholder="coverPlaceholder"
        :to-book-row-view="toBookRowView"
        @item-click="onItemClick"
        @cover-error="onCoverError"
      />
    </div>

    <HistoryEditOverlay
      :edit-mode="editMode"
      :is-all-selected="isAllSelected"
      :selected-count="selectedIds.size"
      :delete-confirm-visible="deleteConfirmVisible"
      :delete-modal-theme="deleteModalTheme"
      @toggle-all="toggleSelectAll"
      @request-delete="openDeleteConfirm"
      @close-delete="closeDeleteConfirm"
      @confirm-delete="confirmDelete"
    />
  </div>
</template>

<style scoped>
.rr-card {
  background: var(--un-preset-theme-colors-n-bg-card);
  border: 1px solid var(--un-preset-theme-colors-n-border-primary);
}
</style>
